'use client';

import { useState } from 'react';
import Link from 'next/link';
import CloudinaryImage from '@/components/ui/CloudinaryImage';
import Icon from '@/components/ui/AppIcon';

interface Equipment {
  id: string;
  title: string;
  slug: string;
  price: number;
  location: string;
  condition: string;
  year: number;
  manufacturer: string;
  category: string;
  images: Array<{ url: string; alt: string }>;
  seller: {
    name: string;
    rating: number;
    verified: boolean;
    phone: string;
  };
  specifications: {
    hours?: number;
    weight?: string;
    power?: string;
  };
  featured: boolean;
  listedDate: string;
}

interface EquipmentCardProps {
  equipment: Equipment;
  viewMode: 'grid' | 'list';
  onWhatsAppClick: (phone: string, title: string) => void;
  onEnquiryClick: (equipmentId: number) => void;
  isAuthenticated: boolean;
}

const EquipmentCard = ({ equipment, viewMode, onWhatsAppClick, onEnquiryClick, isAuthenticated }: EquipmentCardProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === equipment.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? equipment.images.length - 1 : prev - 1
    );
  };

  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onWhatsAppClick(equipment.seller.phone, equipment.title);
  };

  const handleEnquiryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEnquiryClick(equipment.id);
  };

  // Helper function to mask phone number for non-authenticated users
  const getMaskedPhone = (phone: string) => {
    if (isAuthenticated) return phone;
    return phone.substring(0, 3) + '****' + phone.substring(phone.length - 2);
  };

  // Helper function to mask seller name for non-authenticated users
  const getMaskedSellerName = (name: string) => {
    if (isAuthenticated) return name;
    const words = name.split(' ');
    if (words.length > 1) {
      return words[0] + ' ' + words.slice(1).map(word => word.charAt(0) + '***').join(' ');
    }
    return name.charAt(0) + '***';
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-card border border-border rounded-industrial overflow-hidden industrial-shadow hover:shadow-lg transition-industrial">
        <Link href={`/equipment-detail?slug=${equipment.slug}`} className="block">
          <div className="flex flex-col md:flex-row">
            {/* Image Section */}
            <div className="relative w-full md:w-80 h-48 md:h-auto overflow-hidden bg-muted">
              {equipment.featured && (
                <div className="absolute top-3 left-3 z-10">
                  <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
                    Featured
                  </span>
                </div>
              )}
              
              {/* Registration Required Badge */}
              {!isAuthenticated && (
                <div className="absolute top-3 right-3 z-10">
                  <span className="bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
                    Register for Contact
                  </span>
                </div>
              )}
              
              {equipment.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-industrial"
                  >
                    <Icon name="ChevronLeftIcon" size={16} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-industrial"
                  >
                    <Icon name="ChevronRightIcon" size={16} />
                  </button>
                </>
              )}

              <CloudinaryImage
                src={equipment.images[currentImageIndex]?.url}
                alt={equipment.images[currentImageIndex]?.alt}
                fill
                preset="card"
                responsive
                sizes="(max-width: 768px) 100vw, 320px"
                className="object-cover"
              />

              {equipment.images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-1">
                  {equipment.images.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Content Section */}
            <div className="flex-1 p-6">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading text-lg font-bold text-foreground hover:text-primary transition-industrial line-clamp-2">
                      {equipment.title}
                    </h3>
                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-primary">
                        ${equipment.price.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary mb-4">
                    <span className="flex items-center space-x-1">
                      <Icon name="CalendarIcon" size={14} />
                      <span>{equipment.year}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="MapPinIcon" size={14} />
                      <span>{equipment.location}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="TagIcon" size={14} />
                      <span>{equipment.condition}</span>
                    </span>
                    {equipment.specifications.hours && (
                      <span className="flex items-center space-x-1">
                        <Icon name="ClockIcon" size={14} />
                        <span>{equipment.specifications.hours.toLocaleString()} hrs</span>
                      </span>
                    )}
                  </div>

                  {/* Specifications */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {equipment.specifications.weight && (
                      <div>
                        <span className="text-xs text-text-secondary uppercase tracking-wide">Weight</span>
                        <div className="font-medium">{equipment.specifications.weight}</div>
                      </div>
                    )}
                    {equipment.specifications.power && (
                      <div>
                        <span className="text-xs text-text-secondary uppercase tracking-wide">Power</span>
                        <div className="font-medium">{equipment.specifications.power}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-text-secondary uppercase tracking-wide">Category</span>
                      <div className="font-medium">{equipment.category}</div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      {equipment.seller.verified && (
                        <Icon name="CheckBadgeIcon" size={16} className="text-success" />
                      )}
                      <span className="text-sm font-medium">{getMaskedSellerName(equipment.seller.name)}</span>
                      {!isAuthenticated && (
                        <Icon name="LockClosedIcon" size={12} className="text-text-secondary" />
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="StarIcon" size={14} className="text-primary fill-current" />
                      <span className="text-sm">{equipment.seller.rating}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleWhatsAppClick}
                      className={`flex items-center space-x-1 px-3 py-1 rounded text-sm transition-industrial ${
                        isAuthenticated 
                          ? 'bg-green-600 text-white hover:bg-green-700' :'bg-muted border border-border text-text-secondary hover:bg-secondary/10'
                      }`}
                    >
                      <Icon name="ChatBubbleLeftRightIcon" size={14} />
                      <span>{isAuthenticated ? 'WhatsApp' : 'Register to Contact'}</span>
                    </button>
                    <button
                      onClick={handleEnquiryClick}
                      className={`flex items-center space-x-1 px-3 py-1 rounded text-sm transition-industrial ${
                        isAuthenticated 
                          ? 'border border-border hover:bg-muted' :'bg-primary text-primary-foreground hover:bg-accent'
                      }`}
                    >
                      <Icon name="EnvelopeIcon" size={14} />
                      <span>{isAuthenticated ? 'Enquiry' : 'Register to Enquire'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-industrial overflow-hidden industrial-shadow hover:shadow-lg transition-industrial">
      <Link href={`/equipment-detail?slug=${equipment.slug}`} className="block">
        {/* Image Section */}
        <div className="relative h-48 overflow-hidden bg-muted">
          {equipment.featured && (
            <div className="absolute top-3 left-3 z-10">
              <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
                Featured
              </span>
            </div>
          )}

          {/* Registration Required Badge */}
          {!isAuthenticated && (
            <div className="absolute top-3 right-3 z-10">
              <span className="bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
                Register for Contact
              </span>
            </div>
          )}
          
          {equipment.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-industrial"
              >
                <Icon name="ChevronLeftIcon" size={16} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-industrial"
              >
                <Icon name="ChevronRightIcon" size={16} />
              </button>
            </>
          )}

          <CloudinaryImage
            src={equipment.images[currentImageIndex]?.url}
            alt={equipment.images[currentImageIndex]?.alt}
            fill
            preset="card"
            responsive
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover"
          />

          {equipment.images.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-1">
              {equipment.images.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-4">
          {/* Header */}
          <div className="mb-3">
            <h3 className="font-heading text-lg font-bold text-foreground hover:text-primary transition-industrial line-clamp-2 mb-1">
              {equipment.title}
            </h3>
            <div className="text-2xl font-bold text-primary">
              ${equipment.price.toLocaleString()}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Year:</span>
              <span className="font-medium">{equipment.year}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Condition:</span>
              <span className="font-medium">{equipment.condition}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Location:</span>
              <span className="font-medium">{equipment.location}</span>
            </div>
            {equipment.specifications.hours && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Hours:</span>
                <span className="font-medium">{equipment.specifications.hours.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Seller Info */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
            <div className="flex items-center space-x-2">
              {equipment.seller.verified && (
                <Icon name="CheckBadgeIcon" size={16} className="text-success" />
              )}
              <span className="text-sm font-medium">{getMaskedSellerName(equipment.seller.name)}</span>
              {!isAuthenticated && (
                <Icon name="LockClosedIcon" size={12} className="text-text-secondary" />
              )}
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="StarIcon" size={14} className="text-primary fill-current" />
              <span className="text-sm">{equipment.seller.rating}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleWhatsAppClick}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-industrial transition-industrial ${
                isAuthenticated 
                  ? 'bg-green-600 text-white hover:bg-green-700' :'bg-muted border border-border text-text-secondary hover:bg-secondary/10'
              }`}
            >
              <Icon name="ChatBubbleLeftRightIcon" size={16} />
              <span className="font-heading font-bold uppercase tracking-wide text-sm">
                {isAuthenticated ? 'WhatsApp' : 'Register to Contact'}
              </span>
            </button>
            <button
              onClick={handleEnquiryClick}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-industrial transition-industrial ${
                isAuthenticated 
                  ? 'border border-border hover:bg-muted' :'bg-primary text-primary-foreground hover:bg-accent'
              }`}
            >
              <Icon name="EnvelopeIcon" size={16} />
              <span className="font-heading font-bold uppercase tracking-wide text-sm">
                {isAuthenticated ? 'Send Enquiry' : 'Register to Enquire'}
              </span>
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default EquipmentCard;